import { Pencil, Phone, Plus, Star, Trash2, Mail } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { usePermission } from '@/features/auth/store'
import { useDeactivateEmail, useDeactivatePhone } from '../hooks'
import { usePhoneFormSheet } from './PhoneFormSheet'
import { useEmailFormSheet } from './EmailFormSheet'
import type { Customer, EmailDto, PhoneDto } from '../types'

interface ContactRowProps {
  primary?: boolean
  primaryLabel: string
  secondaryLabel?: string | null
  onEdit?: () => void
  onDeactivate?: () => void
  canEdit: boolean
  Icon: React.ComponentType<{ className?: string }>
}

function ContactRow({ primary, primaryLabel, secondaryLabel, onEdit, onDeactivate, canEdit, Icon }: ContactRowProps) {
  return (
    <div className="flex items-center gap-3 border-b py-3 last:border-0">
      <Icon className="size-4 text-muted-foreground" />
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium">{primaryLabel}</span>
          {primary && (
            <Badge variant="default" className="gap-1 text-xs">
              <Star className="size-3 fill-current" />
              Główny
            </Badge>
          )}
        </div>
        {secondaryLabel && <span className="text-xs text-muted-foreground">{secondaryLabel}</span>}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edytuj">
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDeactivate}
            aria-label="Usuń"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

interface PhonesSectionProps {
  customerId: number
  phones: PhoneDto[]
  canEdit: boolean
}

function PhonesSection({ customerId, phones, canEdit }: PhonesSectionProps) {
  const confirm = useConfirm()
  const sheet = usePhoneFormSheet(customerId)
  const deactivate = useDeactivatePhone(customerId)

  const handleDeactivate = async (phone: PhoneDto) => {
    if (phone.id == null) return
    const ok = await confirm({
      title: `Usunąć numer ${phone.value}?`,
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    try {
      await deactivate.mutateAsync(phone.id)
      toast.success('Telefon usunięty')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Błąd usuwania')
    }
  }

  return (
    <section className="rounded-lg border bg-card p-4">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Telefony</h2>
        {canEdit && (
          <Button variant="outline" size="sm" className="gap-2" onClick={sheet.openAdd}>
            <Plus className="size-4" />
            Dodaj
          </Button>
        )}
      </header>
      {phones.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">Brak numerów telefonu.</p>
      ) : (
        phones.map((p) => (
          <ContactRow
            key={p.id ?? p.value}
            Icon={Phone}
            primary={p.isPrimary}
            primaryLabel={p.value}
            secondaryLabel={p.label}
            canEdit={canEdit}
            onEdit={() => sheet.openEdit(p)}
            onDeactivate={() => handleDeactivate(p)}
          />
        ))
      )}
      {sheet.sheet}
    </section>
  )
}

interface EmailsSectionProps {
  customerId: number
  emails: EmailDto[]
  canEdit: boolean
}

function EmailsSection({ customerId, emails, canEdit }: EmailsSectionProps) {
  const confirm = useConfirm()
  const sheet = useEmailFormSheet(customerId)
  const deactivate = useDeactivateEmail(customerId)

  const handleDeactivate = async (email: EmailDto) => {
    if (email.id == null) return
    const ok = await confirm({
      title: `Usunąć email ${email.value}?`,
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    try {
      await deactivate.mutateAsync(email.id)
      toast.success('Email usunięty')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Błąd usuwania')
    }
  }

  return (
    <section className="rounded-lg border bg-card p-4">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Email</h2>
        {canEdit && (
          <Button variant="outline" size="sm" className="gap-2" onClick={sheet.openAdd}>
            <Plus className="size-4" />
            Dodaj
          </Button>
        )}
      </header>
      {emails.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">Brak adresów email.</p>
      ) : (
        emails.map((e) => (
          <ContactRow
            key={e.id ?? e.value}
            Icon={Mail}
            primary={e.isPrimary}
            primaryLabel={e.value}
            secondaryLabel={e.label}
            canEdit={canEdit}
            onEdit={() => sheet.openEdit(e)}
            onDeactivate={() => handleDeactivate(e)}
          />
        ))
      )}
      {sheet.sheet}
    </section>
  )
}

export function CustomerContactsTab({ customer }: { customer: Customer }) {
  const canEdit = usePermission('partners.customer.edit')
  return (
    <div className="flex flex-col gap-4">
      <PhonesSection customerId={customer.id} phones={customer.phones} canEdit={canEdit} />
      <EmailsSection customerId={customer.id} emails={customer.emails} canEdit={canEdit} />
    </div>
  )
}
